import { useReducer } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Labeled, Modal, Select, TextArea, TextInput, Toggle } from '@tes/ui';
import { api } from '../../lib/api';
import { messageOf } from '../../lib/errors';
import { renameVariableInCachedTemplates } from '../../lib/templateCache';
import type { VariableDef, VariableSource, VariableType } from '../../lib/types';
import { useEditor } from '../../editor/editorStore';
import { useVariableGroups, useVariables } from './useVariables';
import {
  deriveForm,
  initialFormState,
  toVariableWrite,
  variableFormReducer,
  type QuestionKind,
} from './variableForm';

export function VariableFormDialog({
  initial,
  onClose,
}: {
  initial: VariableDef | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: allVariables } = useVariables();
  const { data: groups } = useVariableGroups();
  const [s, dispatch] = useReducer(variableFormReducer, initial, initialFormState);
  const patch = (p: Partial<typeof s>) => dispatch({ type: 'patch', patch: p });
  const d = deriveForm(s, initial);

  // Conditions may only reference an UNCONDITIONAL variable (server rule) and
  // never the variable itself.
  const gateCandidates = (allVariables ?? []).filter(
    (v) => v.key !== (initial?.key ?? s.key) && !v.askWhenKey,
  );
  const gate = gateCandidates.find((v) => v.key === s.askWhenKey);

  const mutation = useMutation({
    mutationFn: () => {
      const body = toVariableWrite(s, initial);
      return initial ? api.updateVariable(initial.id, body) : api.createVariable(body);
    },
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: ['variables'] });
      void qc.invalidateQueries({ queryKey: ['variable-groups'] }); // membership shows there
      // The API cascades a rename to every saved draft placement. The open
      // editor holds its own copy of those placements (and the query cache
      // holds another), so rename them here too — or the next save carries a
      // key the library no longer has.
      if (initial && saved.key !== initial.key) {
        useEditor.getState().renameVariableKey(initial.key, saved.key);
        renameVariableInCachedTemplates(qc, initial.key, saved.key);
      }
      onClose();
    },
  });

  return (
    <Modal title={initial ? 'Edit variable' : 'Add variable'} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <Labeled label="Key (snake_case — what n8n sends)">
          <TextInput
            value={s.key}
            onChange={(e) => patch({ key: e.target.value })}
            required
            placeholder="first_name"
            data-autofocus
          />
        </Labeled>
        <Labeled label="Label (shown in the editor)">
          <TextInput
            value={s.label}
            onChange={(e) => patch({ label: e.target.value })}
            required
            placeholder="First Name"
          />
        </Labeled>
        <Labeled label="Source — who supplies the value">
          <Select
            value={s.source}
            onChange={(e) =>
              dispatch({ type: 'setSource', source: e.target.value as VariableSource })
            }
          >
            <option value="auto">Automatic — n8n derives it (CRM, lookups, quote)</option>
            <option value="asked">Asked — a person answers at creation time</option>
          </Select>
        </Labeled>
        {!d.asked && (
          <Labeled label="Type">
            <Select
              value={s.type}
              onChange={(e) => patch({ type: e.target.value as VariableType })}
            >
              <option value="text">Text — merges the value onto the page</option>
              <option value="checkbox">Checkbox — stamps an X when true</option>
            </Select>
          </Labeled>
        )}
        <Toggle
          label={
            d.grouped ? 'Required — decided by the group' : 'Required — creation fails without it'
          }
          checked={d.grouped ? false : s.required}
          disabled={d.grouped}
          onChange={(v) => patch({ required: v })}
        />

        {d.isCheckbox ? (
          <Toggle
            label={
              d.grouped
                ? 'Default: the pre-selected choice of the group'
                : 'Default: ticked (when the value is omitted)'
            }
            checked={s.defaultTicked}
            onChange={(v) => patch({ defaultTicked: v })}
          />
        ) : (
          <Labeled label="Default value (used when the value is omitted)">
            <TextInput
              value={s.defaultValue}
              onChange={(e) => patch({ defaultValue: e.target.value })}
            />
          </Labeled>
        )}

        {d.asked && (
          <div className="space-y-4 rounded-lg border border-violet-200 bg-violet-50/50 p-3">
            <p className="text-xs font-medium text-violet-700">Question shown at creation time</p>

            <Labeled label="Question type">
              <Select
                value={s.qKind}
                onChange={(e) => patch({ qKind: e.target.value as QuestionKind })}
              >
                <option value="yesno">Yes / No — ticks a checkbox on the document</option>
                <option value="text">Short text</option>
                <option value="textarea">Longer text</option>
                <option value="number">Number</option>
                <option value="choice">Single select — pick one from a list</option>
                <option value="multichoice">Multi select — pick any from a list</option>
              </Select>
            </Labeled>

            {d.isCheckbox && (
              <Labeled label="Choice group (one-of selection with other checkboxes)">
                <Select value={s.groupKey} onChange={(e) => patch({ groupKey: e.target.value })}>
                  <option value="">None — standalone yes/no question</option>
                  {(groups ?? []).map((g) => (
                    <option key={g.id} value={g.key}>
                      {g.key} — {g.prompt}
                    </option>
                  ))}
                </Select>
              </Labeled>
            )}

            {d.grouped ? (
              <p className="rounded-lg bg-white p-2 text-xs text-slate-600">
                This box becomes one option of the group&apos;s question — the option text is the{' '}
                <span className="font-medium">label</span> above. Exactly one member of the group
                can be ticked, enforced at creation time.
              </p>
            ) : (
              <>
                <Labeled label="Question (falls back to the label)">
                  <TextInput
                    value={s.prompt}
                    onChange={(e) => patch({ prompt: e.target.value })}
                    placeholder={
                      d.isCheckbox ? 'Is a meter exchange required?' : s.label || undefined
                    }
                  />
                </Labeled>
                <Labeled label="Helper text (optional)">
                  <TextInput
                    value={s.helpText}
                    onChange={(e) => patch({ helpText: e.target.value })}
                  />
                </Labeled>

                {d.withOptions && (
                  <Labeled label="Options (one per line, 2+)">
                    <TextArea
                      rows={3}
                      value={s.optionsText}
                      onChange={(e) => patch({ optionsText: e.target.value })}
                      placeholder={'New\nAlteration'}
                    />
                  </Labeled>
                )}
                {s.qKind === 'multichoice' && (
                  <p className="rounded-lg bg-white p-2 text-xs text-slate-600">
                    Any number of options can be picked; the document prints them as a
                    comma-separated list. A default is also comma-separated (e.g.{' '}
                    <code className="font-mono">New, Alteration</code>).
                  </p>
                )}

                <Labeled label="Only ask when… (optional)">
                  <Select
                    value={s.askWhenKey}
                    onChange={(e) =>
                      dispatch({
                        type: 'setAskWhenKey',
                        key: e.target.value,
                        gateType: gateCandidates.find((v) => v.key === e.target.value)?.type,
                      })
                    }
                  >
                    <option value="">Always asked</option>
                    {gateCandidates.map((v) => (
                      <option key={v.id} value={v.key}>
                        {v.key}
                      </option>
                    ))}
                  </Select>
                </Labeled>
                {s.askWhenKey &&
                  (gate?.type === 'checkbox' ? (
                    <Labeled label={`…${s.askWhenKey} is`}>
                      <Select
                        value={s.askWhenValue}
                        onChange={(e) => patch({ askWhenValue: e.target.value })}
                      >
                        <option value="true">Yes (ticked)</option>
                        <option value="false">No (unticked)</option>
                      </Select>
                    </Labeled>
                  ) : (
                    <Labeled label={`…${s.askWhenKey} equals`}>
                      <TextInput
                        value={s.askWhenValue}
                        onChange={(e) => patch({ askWhenValue: e.target.value })}
                        required
                      />
                    </Labeled>
                  ))}
              </>
            )}
          </div>
        )}

        {d.isCheckbox && !d.asked && (
          <p className="rounded-lg bg-slate-50 p-2 text-xs text-slate-600">
            n8n sends <code className="font-mono">true</code> or{' '}
            <code className="font-mono">false</code> (booleans, <code>&quot;yes&quot;</code>/
            <code>&quot;no&quot;</code> and <code>1</code>/<code>0</code> also work). A true value
            stamps an <span className="font-semibold">X</span>; false leaves the box empty.
          </p>
        )}

        {d.retyping && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
            Changing the type changes how{' '}
            <span className="font-medium">{initial?.usage?.placements}</span> existing placement(s)
            merge, and resets the stored default. Published versions keep the old behaviour until
            you republish them.
          </p>
        )}

        {d.renaming && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
            Renaming updates every draft template using{' '}
            <code className="font-mono">{initial?.key}</code>. Published versions keep the old key
            until you republish — update your n8n payloads to send{' '}
            <code className="font-mono">{s.key}</code>.
          </p>
        )}
        {mutation.error && (
          <p className="text-sm text-red-600">{messageOf(mutation.error, 'Save failed')}</p>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mutation.isPending || !s.key || !s.label}>
            {initial ? 'Save' : 'Add'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
